import { withConditionMonitors } from '@/trendData/conditionRuntime.utilities';
import { withEffectiveYAxisExtremes } from '@/trendData/itemRuntime.utilities';
import { withScalarValues } from '@/trendData/scalarRuntime.utilities';
import { withSeriesValues } from '@/trendData/seriesRuntime.utilities';
import { withTableValues } from '@/trendData/tableRuntime.utilities';
import { ITEM_TYPES } from '@/trendData/trendData.constants';

export interface OverlayableItem {
  id: string;
  itemType?: string;
}

type FetchedDataModifier<K extends ITEM_TYPES> = <T extends { id: string; itemType: K }>(item: T) => T;

type FetchedDataModifiers = Partial<{ [K in ITEM_TYPES]: FetchedDataModifier<K> }>;

/**
 * The runtime store overlay for each item type that keeps fetched data outside its item. Annotated
 * rather than `satisfies`, so the dispatcher below can index it with an arbitrary ITEM_TYPES; the
 * annotation enforces the same per-slot check.
 */
const FETCHED_DATA_MODIFIERS: FetchedDataModifiers = {
  [ITEM_TYPES.CONDITION]: withConditionMonitors,
  [ITEM_TYPES.SCALAR]: withScalarValues,
  [ITEM_TYPES.SERIES]: withSeriesValues,
  [ITEM_TYPES.TABLE]: withTableValues,
};

/**
 * Overlays the fetched data that an item's type keeps in a runtime store, so readers see it
 * alongside the item's config. Item types without a registered modifier are returned as-is.
 */
export function withFetchedData<T extends OverlayableItem>(item: T): T {
  const modify = FETCHED_DATA_MODIFIERS[item.itemType as ITEM_TYPES] as ((item: T) => T) | undefined;
  return modify ? modify(item) : item;
}

/**
 * Overlays the runtime state the chart renders from: an item's computed y-axis extremes and the
 * fetched data for its type. Request status is excluded; the chart does not read it.
 */
export function withChartRuntimeState<T extends OverlayableItem & { isChildOf?: string }>(item: T): T {
  return withFetchedData(withEffectiveYAxisExtremes(item));
}
